import { createReducer } from "typesafe-actions";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";

import {
  Actions,
  createEmailTemplate,
  deleteEmailTemplate,
  fetchEmailQueue,
  fetchEmailQuota,
  fetchEmailSyncProgress,
  fetchEmailTemplates,
  fetchTemplatePreview,
  hideEmailFeatures,
  hideEmailQueue,
  shareEmailTemplate,
  showEmailFeatures,
  showEmailQueue,
  updateEmailTemplate,
} from "./actions";

export interface EmailState {
  emailFeaturesVisible: boolean;
  emailQueue: Email[];
  emailQueueLoading: boolean;
  emailQueueVisible: boolean;
  emailQuota?: EmailQuota;
  emailQuotaUpdating: boolean;
  emailSyncProgress?: EmailSyncProgress;
  templateCreating: boolean;
  templateDeleting: boolean;
  templatePreview?: EmailPreview;
  templatePreviewLoading: boolean;
  templates: EmailTemplate[];
  templatesLoading: boolean;
  templateUpdating: boolean;
}

const initialState: EmailState = {
  emailFeaturesVisible: false,
  emailQueue: [],
  emailQueueLoading: false,
  emailQueueVisible: false,
  emailQuotaUpdating: false,
  templateCreating: false,
  templateDeleting: false,
  templatePreviewLoading: false,
  templates: [],
  templatesLoading: false,
  templateUpdating: false,
};

const email = createReducer<EmailState, Actions>(initialState)
  .handleAction(fetchEmailQuota.request, (state) => ({
    ...state,
    emailQuotaUpdating: true,
  }))
  .handleAction(fetchEmailQuota.success, (state, { payload }) => ({
    ...state,
    emailQuota: payload,
    emailQuotaUpdating: false,
  }))
  .handleAction(fetchEmailQuota.failure, (state) => ({
    ...state,
    emailQuotaUpdating: false,
  }))
  .handleAction(fetchEmailSyncProgress.request, (state) => ({
    ...state,
  }))
  .handleAction(fetchEmailSyncProgress.success, (state, { payload }) => ({
    ...state,
    emailSyncProgress: payload,
  }))
  .handleAction(fetchEmailSyncProgress.failure, (state) => ({
    ...state,
  }))
  .handleAction(fetchEmailQueue.request, (state) => ({
    ...state,
    emailQueueLoading: true,
  }))
  .handleAction(fetchEmailQueue.success, (state, { payload }) => ({
    ...state,
    emailQueue: payload,
    emailQueueLoading: false,
  }))
  .handleAction(fetchEmailQueue.failure, (state) => ({
    ...state,
    emailQueueLoading: false,
  }))
  .handleAction(fetchEmailTemplates.request, (state) => ({
    ...state,
    templatesLoading: true,
  }))
  .handleAction(fetchEmailTemplates.success, (state, { payload }) => ({
    ...state,
    templates: payload,
    templatesLoading: false,
  }))
  .handleAction(fetchEmailTemplates.failure, (state) => ({
    ...state,
    templatesLoading: false,
  }))
  .handleAction(fetchTemplatePreview.request, (state) => ({
    ...state,
    templatePreview: undefined,
    templatePreviewLoading: true,
  }))
  .handleAction(fetchTemplatePreview.success, (state, { payload }) => ({
    ...state,
    templatePreview: payload,
    templatePreviewLoading: false,
  }))
  .handleAction(fetchTemplatePreview.failure, (state) => ({
    ...state,
    templatePreviewLoading: false,
  }))
  .handleAction(createEmailTemplate.request, (state) => ({
    ...state,
    templateCreating: true,
  }))
  .handleAction(createEmailTemplate.success, (state) => ({
    ...state,
    templateCreating: false,
  }))
  .handleAction(createEmailTemplate.failure, (state) => ({
    ...state,
    templateCreating: false,
  }))
  .handleAction(deleteEmailTemplate.request, (state) => ({
    ...state,
    deleting: true,
  }))
  .handleAction(deleteEmailTemplate.success, (state) => ({
    ...state,
    deleting: false,
  }))
  .handleAction(deleteEmailTemplate.failure, (state) => ({
    ...state,
    deleting: false,
  }))
  .handleAction(updateEmailTemplate.request, (state) => ({
    ...state,
    templateUpdating: true,
  }))
  .handleAction(updateEmailTemplate.success, (state) => ({
    ...state,
    templateUpdating: false,
  }))
  .handleAction(updateEmailTemplate.failure, (state) => ({
    ...state,
    creating: false,
    templateUpdating: false,
  }))
  .handleAction(shareEmailTemplate.success, (state, { payload }) => ({
    ...state,
    templates: state.templates.map((template) =>
      template.id === payload.templateId ? { ...template, userIds: payload.userIds } : template
    ),
  }))
  .handleAction(showEmailQueue, (state) => ({
    ...state,
    emailQueueVisible: true,
  }))
  .handleAction(hideEmailQueue, (state) => ({
    ...state,
    emailQueueVisible: false,
  }))
  .handleAction(showEmailFeatures, (state) => ({
    ...state,
    emailFeaturesVisible: true,
  }))
  .handleAction(hideEmailFeatures, (state) => ({
    ...state,
    emailFeaturesVisible: false,
  }));

export * from "./selectors";
export type EmailActions = Actions;

export default email;
